<template>
  <router-link :to="to" class="home-route-portal" :class="{ 'is-open': open, 'opens-up': openUp || upward, 'has-mobile-scene': hasMobileScene }" :aria-label="label" @pointerenter="enter" @pointerleave="hovered = false" @pointerdown="press" @pointerup="release" @pointercancel="release" @focus="focus" @blur="focused = false">
    <span class="portal-surface">
    <span class="portal-heading">{{ title }}<ArrowUpRightIcon /></span>
    <span class="portal-description">{{ description }}</span>
    <span class="portal-scene portal-scene-desktop"><HomeEntryScene :kind="kind" :active="open" /></span>
    <span v-if="hasMobileScene" class="portal-scene portal-scene-mobile"><HomeRouteMobileScene :kind="kind" /></span>
    </span>
  </router-link>
</template>

<script>
import { supportsDesktopHover, watchDesktopHover } from '../../utils/desktopHover';
import HomeEntryScene from './HomeEntryScene.vue';
import HomeRouteMobileScene from './HomeRouteMobileScene.vue';
import ArrowUpRightIcon from './ArrowUpRightIcon.vue';
export default {
  name: 'HomeRoutePortal',
  components: { HomeEntryScene, HomeRouteMobileScene, ArrowUpRightIcon },
  props: { to: String, kind: String, title: String, description: String, label: String, openUp: Boolean },
  data: () => ({ hovered: false, focused: false, pressed: false, upward: false }),
  computed: {
    open() { return this.hovered || this.focused || this.pressed; },
    hasMobileScene() { return this.kind === 'travels' || this.kind === 'traffic'; },
  },
  mounted() { this._stopHover = watchDesktopHover(enabled => { if (!enabled) { this.hovered = false; this.focused = false; } }); },
  beforeUnmount() { this._stopHover?.(); window.clearTimeout(this._pressTimer); },
  methods: {
    place(event) {
      const trigger = event.currentTarget;
      const chapter = trigger.closest('[data-story-chapter]');
      const bottom = Math.min(window.innerHeight, chapter?.getBoundingClientRect().bottom ?? window.innerHeight);
      this.upward = trigger.getBoundingClientRect().bottom + 80 > bottom - 16;
    },
    enter(event) { if (event.pointerType !== 'touch' && supportsDesktopHover()) { this.place(event); this.hovered = true; } },
    focus(event) { if (event.target.matches(':focus-visible') && supportsDesktopHover()) { this.place(event); this.focused = true; } },
    press(event) { if (event.pointerType === 'touch') { window.clearTimeout(this._pressTimer); this.place(event); this.pressed = true; } },
    release() { window.clearTimeout(this._pressTimer); this._pressTimer = window.setTimeout(() => { this.pressed = false; }, 180); },
  },
};
</script>

<style scoped>
.home-route-portal { position: relative; display: block; box-sizing: border-box; width: 100%; height: 110px; margin: 1rem 0; color: var(--ink, #333); text-decoration: none; -webkit-tap-highlight-color: transparent; }
.portal-surface { position: absolute; display: block; box-sizing: border-box; inset: 0 0 auto; height: 110px; padding: 20px 18px; overflow: hidden; border-block: 1px solid var(--line, #ddd); background: transparent; transition: height 420ms cubic-bezier(.22,1,.36,1), top 420ms cubic-bezier(.22,1,.36,1), background-color 180ms ease; }
.home-route-portal::after { display: none; }
.portal-heading { display: flex; align-items: center; gap: .8rem; font: 500 1rem 'Quattrocento Sans', sans-serif; }
.portal-heading :deep(svg) { width: .8rem; opacity: .45; transition: transform 350ms cubic-bezier(.22,1,.36,1), opacity 180ms ease; }
.portal-description { display: block; margin-top: 8px; width: calc(100% - 116px); max-width: 34ch; color: var(--muted, #737373); font: .78rem/1.5 'Quattrocento Sans', sans-serif; transition: opacity 150ms ease, transform 350ms cubic-bezier(.22,1,.36,1); }
.portal-scene { position: absolute; width: 138px; height: 86px; right: 2px; top: 13px; pointer-events: none; transition: width 520ms cubic-bezier(.22,1,.36,1), height 520ms cubic-bezier(.22,1,.36,1), top 520ms cubic-bezier(.22,1,.36,1), right 520ms cubic-bezier(.22,1,.36,1); }
.portal-scene-mobile { display: none; }
.is-open { z-index: 4; }
.is-open .portal-surface { height: 190px; background: var(--surface, #f7f7f5); }
.is-open.opens-up .portal-surface { top: -80px; }
.is-open .portal-description { opacity: 0; transform: translateY(-6px); }
.is-open .portal-scene { width: calc(100% - 36px); height: 128px; top: 47px; right: 18px; }
.is-open .portal-heading :deep(svg) { opacity: 1; transform: translate(3px,-3px); }
.home-route-portal:focus-visible { outline: 1px solid currentColor; outline-offset: 4px; }
.home-route-portal:focus-visible :deep(*) { transition: none !important; }
@media (hover: none), (pointer: coarse), (max-width: 760px) {
  .home-route-portal { height: 110px; }
  .portal-scene { width: 110px; }
  .portal-description { width: calc(100% - 95px); max-width: none; }
}
@media (max-width: 760px) {
  .home-route-portal { --route-scene-edge: rgba(90, 83, 72, .30); --route-scene-detail-opacity: .28; }
  :global(body.dark-mode .home-route-portal) { --route-scene-edge: rgba(232, 223, 203, .30); }
  .portal-description { width: calc(100% - 70px); }
  .portal-scene { width: 78px; right: 4px; }
  .portal-scene :deep(.entry-scene.cv) { --scene-stroke: var(--route-scene-edge); }
  .portal-scene :deep(.entry-scene.cv .paper) { stroke-width: 1.5; }
  .portal-scene :deep(.entry-scene.cv .soft-line) { stroke-width: 1.5; opacity: var(--route-scene-detail-opacity); }
  .has-mobile-scene .portal-scene-desktop { display: none; }
  .has-mobile-scene .portal-scene-mobile { display: flex; align-items: center; justify-content: center; }
  .portal-scene-mobile :deep(.mobile-route-scene) { width: 48px; height: 42px; }
}
@media (prefers-reduced-motion: reduce) {
  .home-route-portal, .home-route-portal * { transition: none !important; }
}
</style>
